import React, { useState } from "react";
import { LayoutDashboard, FileText, Quote, BookOpen, Download, GraduationCap, Layers, Users, Mail, Inbox, Settings as SettingsIcon, LogOut, Menu, KeyRound, Palette, Image as ImageIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SCHEMAS } from "@/admin/schemas";
import CrudManager from "@/admin/CrudManager";
import Dashboard from "@/admin/Dashboard";
import DataTable from "@/admin/DataTable";
import LandingPageManager from "@/admin/LandingPageManager";
import Settings from "@/admin/Settings";
import SiteContent from "@/admin/SiteContent";
import MediaLibrary from "@/admin/MediaLibrary";
import Account from "@/admin/Account";
import AdminLogin from "@/admin/AdminLogin";

const NAV = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "articles", label: "Articles", icon: FileText },
  { key: "principles", label: "Principles", icon: Quote },
  { key: "books", label: "Books", icon: BookOpen },
  { key: "resources", label: "Resources", icon: Download },
  { key: "programs", label: "Programs", icon: GraduationCap },
  { key: "site-content", label: "Site Content", icon: Palette },
  { key: "media", label: "Media Library", icon: ImageIcon },
  { key: "landing", label: "Landing Pages", icon: Layers },
  { key: "leads", label: "Leads", icon: Users },
  { key: "newsletter", label: "Newsletter", icon: Mail },
  { key: "contacts", label: "Contacts", icon: Inbox },
  { key: "settings", label: "Settings", icon: SettingsIcon },
  { key: "account", label: "Account", icon: KeyRound },
];

export default function AdminApp() {
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("dashboard");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  if (user === null) return <div className="min-h-screen flex items-center justify-center"><div className="h-8 w-8 border-2 border-[#0F2C59] border-t-transparent rounded-full animate-spin" /></div>;
  if (user === false || user?.role !== "admin") return <AdminLogin />;

  const render = () => {
    if (tab === "dashboard") return <Dashboard />;
    if (tab === "landing") return <LandingPageManager />;
    if (tab === "settings") return <Settings />;
    if (tab === "site-content") return <SiteContent />;
    if (tab === "media") return <MediaLibrary />;
    if (tab === "account") return <Account />;
    if (tab === "leads") return <DataTable title="Leads" endpoint="/leads" columns={[{ key: "first_name", label: "First Name" }, { key: "email", label: "Email" }, { key: "lead_source", label: "Source" }, { key: "resource_requested", label: "Resource" }, { key: "campaign", label: "Campaign" }, { key: "created_at", label: "Date" }]} />;
    if (tab === "newsletter") return <DataTable title="Newsletter Signups" endpoint="/newsletter" columns={[{ key: "first_name", label: "First Name" }, { key: "email", label: "Email" }, { key: "source", label: "Source" }, { key: "created_at", label: "Date" }]} />;
    if (tab === "contacts") return <DataTable title="Contact Submissions" endpoint="/contacts" columns={[{ key: "name", label: "Name" }, { key: "email", label: "Email" }, { key: "category", label: "Category" }, { key: "message", label: "Message" }, { key: "created_at", label: "Date" }]} />;
    if (SCHEMAS[tab]) return <CrudManager schema={SCHEMAS[tab]} />;
    return null;
  };

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-60 bg-white border-r border-slate-200 flex flex-col transition-transform ${sidebarOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-5 py-5 border-b border-slate-200">
          <div className="font-serif-display text-lg font-bold text-[#111827]">IMPACTFUL <span className="text-[#0F2C59]">MINDSET</span></div>
          <div className="text-xs text-slate-400 mt-0.5">CMS</div>
        </div>
        <nav className="flex-1 py-4 overflow-y-auto im-scrollbar">
          {NAV.map((n) => (
            <button key={n.key} data-testid={`admin-nav-${n.key}`} onClick={() => { setTab(n.key); setSidebarOpen(false); }}
              className={`w-full flex items-center gap-3 px-5 py-2.5 text-sm transition-colors ${tab === n.key ? "bg-[#0F2C59] text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <n.icon size={17} /> {n.label}
            </button>
          ))}
        </nav>
        <div className="border-t border-slate-200 p-4">
          <div className="text-xs text-slate-500 mb-2 truncate">{user.email}</div>
          <button data-testid="admin-logout" onClick={logout} className="w-full flex items-center gap-2 text-sm text-slate-600 hover:text-red-500">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <div className="lg:hidden bg-white border-b border-slate-200 px-4 py-3 flex items-center gap-3">
          <button onClick={() => setSidebarOpen(true)} data-testid="admin-sidebar-toggle"><Menu size={22} /></button>
          <span className="font-serif-display font-bold">Admin</span>
        </div>
        <div className="p-5 sm:p-8">{render()}</div>
      </div>
    </div>
  );
}
